export type ChatHistoryGroupLabel =
  | "Today"
  | "Yesterday"
  | "Last 7 days"
  | "Last 30 days"
  | "Older";

/**
 * Buckets a list of items by their most recent activity date into labelled groups.
 * Prefers `updatedAt` over `createdAt` so that recently-active chats appear in
 * the correct time group. Items without either date fall into the "Older" bucket.
 */
export function groupHistory<
  T extends { createdAt?: string; updatedAt?: string },
>(
  items: T[],
  now = new Date()
): Array<{ label: ChatHistoryGroupLabel; items: T[] }> {
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const MS = 86_400_000;
  const startOfYesterday = new Date(startOfToday.getTime() - MS);
  const start7DaysAgo = new Date(startOfToday.getTime() - 6 * MS);
  const start30DaysAgo = new Date(startOfToday.getTime() - 29 * MS);

  const buckets: Record<ChatHistoryGroupLabel, T[]> = {
    Today: [],
    Yesterday: [],
    "Last 7 days": [],
    "Last 30 days": [],
    Older: [],
  };

  for (const item of items) {
    const dateStr = item.updatedAt ?? item.createdAt;
    if (!dateStr) {
      buckets.Older.push(item);
      continue;
    }
    const d = new Date(dateStr);
    if (d >= startOfToday) {
      buckets.Today.push(item);
    } else if (d >= startOfYesterday) {
      buckets.Yesterday.push(item);
    } else if (d >= start7DaysAgo) {
      buckets["Last 7 days"].push(item);
    } else if (d >= start30DaysAgo) {
      buckets["Last 30 days"].push(item);
    } else {
      buckets.Older.push(item);
    }
  }

  const order: ChatHistoryGroupLabel[] = [
    "Today",
    "Yesterday",
    "Last 7 days",
    "Last 30 days",
    "Older",
  ];
  return order
    .filter((label) => buckets[label].length > 0)
    .map((label) => ({ label, items: buckets[label] }));
}
